import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Output,
  QueryList,
  ViewEncapsulation,
} from '@angular/core';
import { defaults, map } from 'lodash';
import {
  BehaviorSubject,
  filter,
  merge,
  map as rxMap,
  startWith,
  switchMap,
} from 'rxjs';

import {
  BlockDef,
  BlockTypes,
  DocumentBuilderData,
  PageData,
} from '../interfaces';
import { DocumentBuilderContentComponent } from './document-builder-content.component';
import { DocumentBuilderPageComponent } from './document-builder-page.component';

@Component({
    selector: 'la-document-builder',
    templateUrl: './document-builder.component.html',
    styleUrls: ['./document-builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DocumentBuilderComponent implements AfterContentInit {
  private pagesData$ = new BehaviorSubject<PageData>({});

  private blocks$ = new BehaviorSubject<BlockDef[]>([]);

  private editorModel$ = new BehaviorSubject<Record<string, string>>({});

  private get data(): DocumentBuilderData {
    return {
      blocks: map(this.blocks$.value, (block) =>
        block.type === BlockTypes.EDITOR
          ? defaults({ initialValue: this.editorModel$.value[block.id] }, block)
          : block
      ),
      pagesModel: this.pagesData$.value,
      editorModel: this.editorModel$.value,
    };
  }

  @ContentChild(DocumentBuilderContentComponent) documentContent:
    | DocumentBuilderContentComponent
    | undefined;
  @ContentChildren(DocumentBuilderPageComponent, { descendants: true })
  pages!: QueryList<DocumentBuilderPageComponent>;

  @Output() builderSubmit = new EventEmitter<DocumentBuilderData>();

  ngAfterContentInit(): void {
    this.syncPageData();

    this.documentContent?.blocksChanges$.subscribe((blocks) => {
      this.blocks$.next(blocks);
    });

    this.documentContent?.form.valueChanges
      .pipe(startWith(this.documentContent.form.value))
      .subscribe((values) => {
        this.editorModel$.next(values);
      });
  }

  submit() {
    this.builderSubmit.emit(this.data);
  }

  private syncPageData(): void {
    this.pages.changes
      .pipe(
        startWith(null),
        switchMap(() => {
          const dynamicPagesComponents = map(
            this.pages.toArray(),
            (pageComponent) => pageComponent.dynamicPage$
          );
          const valueChanges$ = map(dynamicPagesComponents, (dynamicPage$) =>
            dynamicPage$.pipe(
              startWith(dynamicPage$.value),
              filter(Boolean),
              switchMap((dynamicPage) =>
                dynamicPage.formGroup.valueChanges.pipe(
                  rxMap((values) => ({
                    pageId: dynamicPage.pageId,
                    values,
                  }))
                )
              )
            )
          );
          return merge(...valueChanges$);
        })
      )
      .subscribe((value) => {
        this.pagesData$.next(
          defaults({ [value.pageId]: value.values }, this.pagesData$.value)
        );
      });
  }
}
